/// useTranslation not needed
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Alert, Card, CardBody, CardTitle } from 'reactstrap'

import Loading from '../Loading'
import SubmitButton from '../SubmitButton'
import posthog from '../../api/posthog'
import { useUpdateCollectionRequestStatusMutation } from '../../store/apiSlice'
import { useTypedSelector } from '../../store/store'
import { getText } from '../../core/i18n'

interface Props {
  request: ICollectionRequest
}

const EditRequestForm: React.FC<Props> = ({ request }) => {
  const navigate = useNavigate()

  const [updateRequest, { isLoading: isUpdatingRequest }] =
    useUpdateCollectionRequestStatusMutation()

  const { watch, handleSubmit, setValue, reset, register, formState } =
    useForm<ICollectionRequest>({
      defaultValues: {
        ...request,
        reviewed_at: request.reviewed_at
          ? new Date(request.reviewed_at).toISOString().slice(0, 16)
          : undefined
      },
    })

  const fieldError = useMemo(() => {
    const keys = Object.keys(formState.errors)
    return keys ? keys.join(', ') : undefined
  }, [formState.errors])

  const collectionTitle = useMemo(() => {
    return getText(request._collection.title)
  }, [request._collection])

  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const reviewedBy = watch('reviewed_by')

  const onSubmit = async (values: ICollectionRequest) => {
    try {
      await updateRequest({
        ...values,
        _id: values._id ?? ""
      })
      reset(values)
      navigate('/admin/requests')
    } catch (err) {
      const error = err as Error
      setError(true)
      setErrorMessage(`Problem updating request: ${error.message}`)
    }
  }

  useEffect(() => {
    posthog.capture('$pageview')
  }, [])

  const onDismiss = () => {
    setError(false)
    setErrorMessage('')
  }

  return (
    <>
      <div className="container p-3">
        <div className="row">
          <div className="col col-12 col-lg-6 offset-lg-3">
            <Card className="shadow">
              <CardBody>
                <CardTitle>
                  <h2>Edit Collection Request</h2>
                </CardTitle>
                <form onSubmit={handleSubmit(onSubmit)} className="mb-5">
                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="collection">
                      Collection
                    </label>
                    <input
                      id="collection"
                      className="form-control"
                      value={collectionTitle}
                      disabled
                    />
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="requester">
                      Requester
                    </label>
                    <input
                      id="requester"
                      className="form-control"
                      value={request.requester.name}
                      disabled
                    />
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="request_type">
                      Request Type
                    </label>
                    <select
                      {...register('request_type')}
                      className="form-select"
                      id="request_type"
                    >
                      <option value="sell">Sell</option>
                      <option value="rent">Rent</option>
                      <option value="feature">Feature</option>
                    </select>
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="status">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="form-select"
                      id="status"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="in_review">In Review</option>
                    </select>
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="message">
                      Message
                    </label>
                    <textarea
                      id="message"
                      className="form-control"
                      placeholder="Request message"
                      {...register('message')}
                      rows={3}
                    />
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="admin_notes">
                      Admin Notes
                    </label>
                    <textarea
                      id="admin_notes"
                      className="form-control"
                      placeholder="Internal admin notes"
                      {...register('admin_notes')}
                      rows={3}
                    />
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="reviewed_by">
                      Reviewed By
                    </label>
                    <input
                      id="reviewed_by"
                      className="form-control"
                      placeholder="Reviewer ID or email"
                      value={reviewedBy?.email}
                      disabled
                    />
                  </div>

                  <div className="input-group mb-3">
                    <label className="input-group-text" htmlFor="reviewed_at">
                      Reviewed At
                    </label>
                    <input
                      id="reviewed_at"
                      className="form-control"
                      type="datetime-local"
                      {...register('reviewed_at', {
                        valueAsDate: true
                      })}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        const now = new Date()
                        const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
                          .toISOString()
                          .slice(0, 16)
                        setValue('reviewed_at', localDateTime, { shouldDirty: true })
                      }}
                    >
                      Now
                    </button>
                  </div>

                  <SubmitButton
                    buttonClassName="mt-3 w-100"
                    color={!formState.isDirty ? 'dark' : 'primary'}
                    disabled={!formState.isValid || formState.isSubmitting}
                    invalid={!formState.isValid}
                    submitting={formState.isSubmitting}
                    error={fieldError}
                  />
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
        <Alert
          color="danger"
          className="fixed-bottom m-5"
          isOpen={Boolean(error)}
          toggle={onDismiss}
        >
          {errorMessage}
        </Alert>
      </div>
    </>
  )
}

export default EditRequestForm
